import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { data, dataHora, mesAno, numero, plural, rotulo } from '../formato';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';
import { Aviso, Carregando, ErroDaApi, LinkProtegido, Rn, Selo } from '../ui';
import {
  FormAnaliseCritica, FormAprovarAnalise, FormBaixa, FormCertificado,
  FormCorrigirFicha, FormPosse, FormSensor, FormSoftware,
} from './EquipamentoForms';
import {
  AbasDaFicha, CartoesPorGrandeza, FaixaDeResumo, LinhaDoTempo, PossoUsar,
  SeloFornecedor, SeloPdf, abaDoEndereco, blocosDeGrandeza,
  type BlocoDeGrandeza, type Evento, type Motivo, type ResumoDaFicha,
} from './EquipamentoAbas';
import { MenuDaOs } from './OsMoldura';
import {
  SecaoAutorizacoes, SecaoFotos, SecaoManutencao,
} from './EquipamentoManutencao';
import { ExcluirDeVez } from './ExclusaoDefinitiva';
import {
  GraficoDaCurva, ResumoDoAjuste, ajusteDaCurva, lerPolinomio,
} from './CurvaDoPicnometro';
import { NomeDaAmostra } from '../NomeDaAmostra';

/**
 * A ficha metrológica de um equipamento.
 *
 * A ordem das seções é a ordem da pergunta que o técnico faz:
 *
 *  1. **posso usar isto hoje?** — a situação, no topo, com o motivo
 *     escrito quando a resposta é não. E o motivo pode estar DENTRO do
 *     equipamento: a `RN-158` reprova a prensa por causa de um sensor;
 *  2. **que número o ensaio vai usar?** — os valores vigentes, que são o
 *     divisor da massa específica e da expansão. Aqui não aparece o
 *     nominal de catálogo: aparece o aferido;
 *  3. **quais sensores tem dentro?** — célula de carga, LVDT, transdutor,
 *     cada um com o próprio certificado e a própria validade;
 *  4. **de onde esse número saiu?** — a aferição que o publicou, com os
 *     itens medidos e a tolerância de cada um;
 *  5. **e a calibração externa?** — o histórico inteiro, com o PDF de
 *     cada certificado e a análise crítica de cada um.
 *
 * O veredito de cada item vem do banco (`D-21`). A tela mostra o valor
 * medido ao lado da faixa e deixa o leitor conferir com os próprios olhos
 * — foi um "Sim" digitado à mão que deixou o cilindro 21 passar 0,015 mm
 * abaixo do mínimo da NBR 7182.
 */

interface Valor {
  id: string; grandeza: string; valor: string; unidade: string | null;
  vigencia_inicio: string; vigencia_fim: string | null;
  certificado: string | null; data_afericao: string | null;
  /* O valor saiu de outros dois do mesmo certificado (`0104`) — o
     volume do anel, que a geometria dá e o papel não imprime. */
  derivado?: boolean; observacoes?: string | null;
}

interface Afericao {
  id: string; data: string; resultado: string; conforme: boolean | null;
  proxima_verificacao: string | null; uso_autorizado: boolean;
  uso_justificativa: string | null; observacoes: string | null;
  certificado: string | null; certificado_titulo: string | null;
  data_afericao: string | null; responsavel: string | null;
  itens: string; itens_nao_conformes: string;
}

interface Item {
  id: string; verificacao_id: string; item: string; simbolo: string | null;
  valor_medido: string | null; unidade: string | null;
  tolerancia_min: string | null; tolerancia_max: string | null;
  /* TRÊS estados. `conforme` nulo é «medido, sem critério» — nunca «fora». */
  conforme: boolean | null;
  veredito: 'conforme' | 'fora' | 'sem_criterio';
  /*
   * Por que não há julgamento (`0098`). «Sem critério» sozinho não diz
   * se a pergunta está fechada — a norma é silenciosa — ou aberta — a
   * norma fala e o certificado não copiou a tolerância. Para quem
   * audita, são coisas diferentes.
   */
  motivo_sem_criterio: string | null;
  derivado: boolean; criterios: number;
  /* Com que instrumento este número foi medido, e com quantas casas. */
  padrao: string | null;
  padrao_certificado: string | null;
  padrao_casas_decimais: number | null;
}

export interface Calibracao {
  id: string; numero_certificado: string; data_calibracao: string;
  valida_de: string | null; valida_ate: string | null; vigente: boolean;
  resultado: string; aprovada: boolean;
  faixa_calibrada: string | null; erro: string | null; incerteza: string | null;
  criterio_aceitacao: string | null; rastreabilidade_padroes: string | null;
  arquivo_id: string | null; empresa: string | null;
  /** A grandeza calibrada — é por ela que a ficha agrupa (`onda 94`). */
  grandeza: string;
  /** `true` quando o certificado é da máquina; `false` quando é de um sensor. */
  da_maquina: boolean;
  de_quem: string;
  de_quem_codigo: string;
  de_quem_nome: string;
  /** O fornecedor homologado e a situação dele hoje (onda 351). */
  fornecedor_id?: string | null;
  fornecedor?: string | null;
  fornecedor_estado?: string | null;
  fornecedor_ate?: string | null;
}

export interface Analise {
  id: string; calibracao_id: string;
  uso_pretendido: string;
  faixa_uso_min: string | null; faixa_uso_max: string | null;
  faixa_uso_unidade: string | null;
  ema: string; ema_unidade: string; ema_origem: string;
  maior_erro: string; incerteza: string; fator_abrangencia: string | null;
  regra_decisao: string; regra_justificativa: string | null;
  margem: string; conforme: boolean;
  rastreabilidade_ok: boolean; rastreabilidade_nota: string | null;
  fator_correcao: string | null; correcao_aplicada: boolean;
  correcao_onde: string | null;
  conclusao: string; restricoes: string | null; observacoes: string | null;
  analisado_por: string;
  analisado_por_nome: string | null; analisado_em: string;
  aprovado_por_nome: string | null; aprovado_em: string | null;
}

export interface Componente {
  id: string; pai_id: string; codigo_patrimonial: string; nome: string;
  categoria: string; fabricante: string | null; modelo: string | null;
  numero_serie: string | null;
  grandeza_medida: string | null; posicao: string | null;
  /* O que o sensor alcança e com que passo — a pergunta que vem logo
     depois de «mede o quê». Sem faixa, «apto» não diz apto para quê. */
  faixa_min: string | null; faixa_max: string | null; faixa_unidade: string | null;
  resolucao: string | null; resolucao_unidade: string | null;
  capacidade_nominal: string | null; capacidade_unidade: string | null;
  calibravel: boolean; status: string;
  baixa_tipo: string | null;
  calibracao_vigente: boolean | null; calibracao_ate: string | null;
  apto: boolean;
}

interface Uso {
  ensaio_id: string; status_ensaio: string;
  amostra: string | null; os: string | null; relatorio: string | null;
  /** Onda 303 (RN-248): o nome que o cliente deu à amostra. */
  amostra_identificacao?: string | null;
  datahora_uso: string | null; data_validade: string | null;
}

interface Impacto { usos: Uso[]; total: number; emitidos: number }

/** Um trecho do histórico de posse (`0093`). */
interface Posse {
  id: string; regime: string; regime_rotulo: string; proprio: boolean;
  titular: string | null; documento: string | null;
  vigencia_inicio: string; vigencia_fim: string | null;
  observacoes: string | null; vigente: boolean;
}

/**
 * A curva de calibração (`0100`).
 *
 * Há grandeza que não é número: a massa do picnômetro cheio cai quando a
 * temperatura sobe. O certificado publica os pontos medidos e a função
 * ajustada — e a faixa que os pontos cobrem, que é o que separa ler a
 * curva de extrapolá-la.
 */
/**
 * Um elo da trilha de auditoria (`CA-21`).
 *
 * A ficha passou a poder ser corrigida (`0102`). A partir daí, «a
 * resolução desta balança é 0,001 g» deixou de ser um fato e passou a
 * ser um fato COM DATA — e quem lê o laudo de agosto precisa poder ver
 * que o número mudou em setembro, quem mudou e por quê.
 */
interface Elo {
  operacao: string; datahora: string;
  usuario_nome: string | null; usuario_email: string | null;
  perfil: string | null; motivo: string | null;
  campos_alterados: string[] | null;
  valor_anterior: Record<string, unknown> | null;
  valor_novo: Record<string, unknown> | null;
}

interface Curva {
  curva_id: string; grandeza: string; versao: number;
  descricao: string | null; variavel_entrada: string | null;
  entrada_unidade: string; saida_unidade: string;
  interpolacao: string; funcao_texto: string | null; r2: number | null;
  vigencia_inicio: string; vigencia_fim: string | null; vigente: boolean;
  certificado: string | null; pontos: { x: number; y: number }[];
  x_min: number | null; x_max: number | null;
  /** RN-259: o ajuste que o banco mantém (grau 2, mínimos quadrados). */
  ajuste_grau?: number | null;
  ajuste_coeficientes?: (number | string)[] | null;
  ajuste_r2?: number | string | null;
  ajuste_residuo_max?: number | string | null;
  ajuste_x_min?: number | string | null;
  ajuste_x_max?: number | string | null;
}

interface Metrologia {
  equipamento: Record<string, unknown>;
  valores: Valor[];
  afericoes: Afericao[];
  itens: Item[];
  calibracoes: Calibracao[];
  componentes: Componente[];
  analises: Analise[];
  curvas: Curva[];
  resumo?: ResumoDaFicha | null;
  situacao: {
    apto: boolean; impedimentos: string[]; ressalvas: string[];
    /** Onda 351: o código de cada motivo, para a ação que resolve. */
    motivos?: Motivo[];
  };
}

/**
 * Anexar o PDF a um certificado que entrou sem ele.
 *
 * O parque real chegou por carga: número, data e rastreabilidade lidos
 * do papel, e o papel na pasta. Sem este botão, «anexar os 116 PDFs»
 * seria recadastrar 116 calibrações — e jogar fora a trilha de quem
 * lançou o quê.
 *
 * O seletor abre direto no clique, sem diálogo: é uma ação de um passo,
 * e um diálogo para escolher um arquivo é um passo a mais entre a
 * pessoa e a pasta que ela já tem aberta.
 */
function AnexarPdf({ calibracao, aoAnexar }: {
  calibracao: Calibracao; aoAnexar(): void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const { pode } = useSessao();

  if (!pode('arquivo.enviar')) {
    /* Sem permissão de anexar, o selo ao lado já disse que não há PDF —
       repetir em palavras aqui só encomprida a linha. */
    return null;
  }

  async function enviar(arquivo: File) {
    setErro(null);
    setEnviando(true);
    try {
      const form = new FormData();
      form.append('certificado', arquivo, arquivo.name);
      await api(`/equipamentos/calibracoes/${calibracao.id}/certificado`,
        { metodo: 'POST', formulario: form });
      aoAnexar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível anexar.');
    } finally { setEnviando(false); }
  }

  /*
   * Sem a frase «Certificado sem PDF anexado».
   *
   * Ela vinha de quando não havia ícone. Com o selo ▢ PDF ao lado, a
   * linha ficou «▢ PDF Certificado sem PDF anexado Anexar o PDF»: três
   * maneiras de dizer a mesma coisa, e a do meio não é ação nem título.
   * O selo diz o estado; o botão diz o que fazer.
   */
  return (
    <>
      <label className="botao discreto">
        {enviando ? 'Anexando…' : 'Anexar o PDF'}
        <input
          type="file"
          accept="application/pdf"
          hidden
          disabled={enviando}
          onChange={(ev) => {
            const f = ev.currentTarget.files?.[0];
            ev.currentTarget.value = '';
            if (f) void enviar(f);
          }}
        />
      </label>
      {erro && <span className="erro" role="alert"> {erro}</span>}
    </>
  );
}

/** Número com as casas que a grandeza merece, sem zeros de enfeite. */
const g = (v: unknown, unidade?: string | null, casas = 6) =>
  `${numero(v, casas, true)}${unidade ? ` ${unidade}` : ''}`;

/**
 * Como cada origem de periodicidade se lê (`0108`).
 *
 * «Deduzida» não entra aqui: ela ganha selo de atenção na ficha, porque
 * é a única das cinco que pede uma ação — alguém confirmar.
 */
const ORIGEM_DA_PERIODICIDADE: Record<string, string> = {
  certificado: 'o certificado diz',
  norma: 'a norma fixa',
  fabricante: 'o manual do fabricante',
  politica_interna: 'decisão da Testesolo',
  inferida: 'deduzida do histórico',
};

function Faixa({ i }: { i: Item }) {
  const min = i.tolerancia_min;
  const max = i.tolerancia_max;
  if (min === null && max === null) return <span className="nada">sem tolerância</span>;
  if (min !== null && max !== null) {
    return <>{numero(min, 3, true)} a {numero(max, 3, true)}</>;
  }
  return <>{min !== null ? `≥ ${numero(min, 3, true)}` : `≤ ${numero(max!, 3, true)}`}</>;
}

/**
 * A faixa do sensor, escrita como o certificado escreve.
 *
 * Cai para a capacidade nominal quando a faixa não foi preenchida: uma
 * célula de 5000 kgf sem faixa cadastrada ainda diz mais que um traço.
 */
function FaixaDoSensor({ c }: { c: Componente }) {
  const u = c.faixa_unidade ?? c.capacidade_unidade;
  if (c.faixa_min !== null && c.faixa_max !== null) {
    return <>{numero(c.faixa_min, 3, true)} a {numero(c.faixa_max, 3, true)}{u ? ` ${u}` : ''}</>;
  }
  if (c.capacidade_nominal !== null) {
    return <>0 a {numero(c.capacidade_nominal, 3, true)}{u ? ` ${u}` : ''}</>;
  }
  return <span className="nada">—</span>;
}

/**
 * O ícone de PDF anexado.
 *
 * «Deixe um ícone pequeno indicando um arquivo pdf anexado para os itens
 *  do histórico de calibração e histórico de manutenção» — Humberto,
 * 01/09/2026.
 *
 * Numa lista de dez certificados, «tem papel» e «não tem papel» é a
 * diferença entre rastreabilidade completa e um número que ninguém pode
 * conferir — e hoje isso só se descobria lendo o texto de cada linha.
 * Um símbolo diz a mesma coisa de relance.
 *
 * Não é cor sozinha: leva o glifo e o `title`, porque a tela tem de
 * funcionar em preto e branco e para quem não distingue as duas
 * (requisito de acessibilidade do §18).
 */
/* O selo do PDF mora em `EquipamentoAbas.tsx` desde a 351; a manutenção ainda o importa daqui. */
export { SeloPdf };

/**
 * ─── Calibração externa, agrupada por GRANDEZA ─────────────────────────
 *
 * «Hoje os sensores (e os seus certificados) ficam separados de
 *  calibrações externas. Deveriam estar juntos, pois ambos são
 *  calibrações externas. Um olhar menos acurado pode achar que apenas o
 *  deslocamento foi calibrado.» — Humberto, 01/09/2026.
 *
 * ── Por que a grandeza é o eixo, e não o objeto ───────────────────────
 *
 * Porque é a pergunta que o ensaio faz. A MP50 mede FORÇA por uma célula
 * de carga e DESLOCAMENTO por si mesma: agrupar por objeto responde
 * «quantos certificados a máquina tem», que ninguém pergunta; agrupar por
 * grandeza responde «a força está calibrada hoje?», que é o que decide se
 * o ensaio pode começar.
 *
 * ── E por que o sensor sem certificado aparece ────────────────────────
 *
 * `RN-158`: o conjunto vale pelo pior deles. Um bloco de grandeza que só
 * listasse certificados esconderia justamente o caso perigoso — o sensor
 * que mede aquela grandeza e não tem papel nenhum. Ele entra no bloco, em
 * vermelho, dizendo o que falta.
 */
function SecaoCalibracao({
  m, comBaixa, calibravel, analisePorCal, aoNovoCertificado, aoNovaAnalise,
  aoNovoSensor, papel, carregar,
}: {
  m: Metrologia;
  comBaixa: boolean;
  calibravel: boolean;
  analisePorCal: Map<string, Analise>;
  aoNovoCertificado(): void;
  aoNovaAnalise(c: Calibracao): void;
  aoNovoSensor(): void;
  papel: string;
  carregar(): void;
}) {
  const blocos = useMemo<BlocoDeGrandeza[]>(
    () => blocosDeGrandeza(m.calibracoes, m.componentes),
    [m.calibracoes, m.componentes]);

  const historico = m.calibracoes.filter((c) => !c.vigente);
  const temAlgo = m.calibracoes.length > 0 || m.componentes.length > 0;

  if (!calibravel && !temAlgo) return null;

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Calibração externa</h2>
        {!comBaixa && (
          <>
            {papel !== 'componente' && (
              <button type="button" className="botao discreto"
                onClick={aoNovoSensor}>
                + Sensor
              </button>
            )}
            <button type="button" className="botao discreto"
              onClick={aoNovoCertificado}>
              + Certificado
            </button>
          </>
        )}
      </div>
      <p className="sub">
        Por <b>grandeza</b>, e não por objeto: a máquina calibra umas e os
        sensores dela calibram outras, e é a grandeza que o ensaio consulta.
        O conjunto vale pelo pior deles{' '}
        <Rn codigo="RN-158"
          diz="Equipamento composto só está apto se todos os componentes estiverem." />.
        O histórico inteiro fica — certificado novo é <b>registro novo</b>,
        nunca substituição do anterior.
      </p>

      {blocos.length === 0
        ? <p className="nada">Nenhum certificado registrado.</p>
        : blocos.map((b) => (
          <div key={b.grandeza}
            className={`grandeza${b.apto ? '' : ' com-pendencia'}`}>
            <div className="linha-topo">
              <h3 className="cresce">{rotulo(b.grandeza)}</h3>
              {b.vence && (
                <span className="sub mono">até {data(b.vence)}</span>
              )}
              <Selo tom={b.apto ? 'ok' : 'critico'}>
                {b.apto ? 'calibrada' : 'não responde'}
              </Selo>
            </div>

            {/* Os certificados vigentes desta grandeza. */}
            {b.certificados.filter((c) => c.vigente).map((c) => (
              <CertificadoNaGrandeza
                key={c.id}
                c={c}
                analise={analisePorCal.get(c.id)}
                comBaixa={comBaixa}
                aoNovaAnalise={aoNovaAnalise}
                carregar={carregar}
              />
            ))}

            {b.certificados.filter((c) => c.vigente).length === 0 && (
              <p className="nada">
                Nenhum certificado vigente para esta grandeza.
              </p>
            )}

            {/*
              * O sensor que mede esta grandeza e não tem papel. É o caso
              * que o cartão separado escondia, e o motivo de tudo isto.
              */}
            {b.descobertos.length > 0 && (
              <Aviso tipo="erro"
                titulo={`${plural(b.descobertos.length, 'sensor', 'sensores')} sem calibração vigente`}>
                <ul className="lista-sensores-sem">
                  {b.descobertos.map((sn) => (
                    <li key={sn.id}>
                      <Link to={`/equipamento/${sn.id}`}>
                        <b className="mono">{sn.codigo_patrimonial}</b>
                      </Link>
                      {' — '}
                      {sn.nome}
                      {sn.calibracao_ate
                        ? ` · venceu em ${data(sn.calibracao_ate)}`
                        : ' · nunca calibrado'}
                    </li>
                  ))}
                </ul>
                Enquanto um deles não responder, o conjunto inteiro não
                responde por esta grandeza.
              </Aviso>
            )}

            {/* Quem mede esta grandeza, e com que alcance. */}
            {b.sensores.length > 0 && (
              <div className="rola">
                <table className="dados compacta">
                  <thead>
                    <tr>
                      <th>Sensor</th><th>Posição</th>
                      <th>Faixa</th><th>Resolução</th>
                      <th>Calibração até</th><th>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.sensores.map((c) => (
                      <tr key={c.id} className={c.apto ? undefined : 'linha-alerta'}>
                        <td>
                          <Link to={`/equipamento/${c.id}`}>
                            <b className="mono">{c.codigo_patrimonial}</b>
                          </Link>
                          <div className="sub">
                            {c.nome}
                            {c.fabricante && ` · ${c.fabricante}`}
                            {c.numero_serie && ` · nº ${c.numero_serie}`}
                          </div>
                        </td>
                        <td>{c.posicao ?? <span className="nada">—</span>}</td>
                        <td className="num"><FaixaDoSensor c={c} /></td>
                        <td className="num">
                          {c.resolucao
                            ? numero(c.resolucao, 4, true)
                              + (c.resolucao_unidade ? ` ${c.resolucao_unidade}` : '')
                            : <span className="nada">—</span>}
                        </td>
                        <td>
                          {c.calibracao_ate
                            ? data(c.calibracao_ate)
                            : <span className="nada">sem certificado</span>}
                        </td>
                        <td>
                          <Selo tom={c.apto ? 'ok' : 'critico'}>
                            {c.apto ? 'apto' : 'impede o conjunto'}
                          </Selo>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}

      {/*
        * ── O histórico ────────────────────────────────────────────────
        *
        * «Quando for adicionado um novo certificado de calibração, os
        *  antigos permanecerem disponíveis no histórico do equipamento
        *  com a data (incluindo o arquivo pdf)» — Humberto, 01/09/2026.
        *
        * Recolhido, e não escondido: quem abre a ficha antes de um ensaio
        * quer o que vale HOJE; quem refaz a conta de um laudo de 2026 em
        * 2029 quer o que valia lá. As duas leituras cabem, e a segunda
        * não pode atrapalhar a primeira.
        */}
      {historico.length > 0 && (
        <details className="historico-calibracao">
          <summary>
            Histórico — {historico.length} certificado(s) encerrado(s)
          </summary>
          <div className="rola">
            <table className="dados compacta">
              <thead>
                <tr>
                  <th>Certificado</th><th>Grandeza</th><th>De quem</th>
                  <th>Período</th><th>Faixa</th><th>Papel</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((c) => (
                  <tr key={c.id}>
                    <td className="mono">{c.numero_certificado}</td>
                    <td>{rotulo(c.grandeza)}</td>
                    <td>
                      {c.da_maquina
                        ? <span className="sub">a própria máquina</span>
                        : (
                          <Link to={`/equipamento/${c.de_quem}`}>
                            <span className="mono">{c.de_quem_codigo}</span>
                          </Link>
                        )}
                    </td>
                    <td className="mono">
                      {data(c.data_calibracao)} a {data(c.valida_ate)}
                    </td>
                    <td>{c.faixa_calibrada ?? <span className="nada">—</span>}</td>
                    <td>
                      {c.arquivo_id
                        ? (
                          <LinkProtegido
                            caminho={`/arquivos/${c.arquivo_id}/conteudo`}
                            nomeQueda={`certificado-${c.numero_certificado}.pdf`}
                          >
                            <SeloPdf tem />
                          </LinkProtegido>
                        )
                        : <SeloPdf tem={false} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

/** Um certificado vigente dentro do bloco da grandeza. */
function CertificadoNaGrandeza({
  c, analise, comBaixa, aoNovaAnalise, carregar,
}: {
  c: Calibracao;
  analise: Analise | undefined;
  comBaixa: boolean;
  aoNovaAnalise(c: Calibracao): void;
  carregar(): void;
}) {
  return (
    <div className="bloco">
      <div className="linha-topo">
        <b className="cresce mono">{c.numero_certificado}</b>
        {/* De quem é o papel — a informação que faltava. */}
        <Selo tom="neutro">
          {c.da_maquina ? 'a própria máquina' : c.de_quem_codigo}
        </Selo>
        <Selo tom={c.resultado === 'aprovado' ? 'ok'
          : c.resultado === 'reprovado' ? 'critico' : 'atencao'}>
          {rotulo(c.resultado)}
        </Selo>
        <SeloFornecedor c={c} />
      </div>
      <div className="meta">
        {data(c.data_calibracao)} a {data(c.valida_ate)}
        {c.empresa && ` · ${c.empresa}`}
        {c.faixa_calibrada && ` · faixa ${c.faixa_calibrada}`}
      </div>

      <p className="rodape-regra">
        {c.arquivo_id
          ? (
            <LinkProtegido
              caminho={`/arquivos/${c.arquivo_id}/conteudo`}
              nomeQueda={`certificado-${c.numero_certificado}.pdf`}
            >
              <SeloPdf tem /> Abrir o certificado
            </LinkProtegido>
          )
          : (
            <>
              <SeloPdf tem={false} />
              {' '}
              <AnexarPdf calibracao={c} aoAnexar={carregar} />
            </>
          )}
        {!analise && !comBaixa && (
          <button
            type="button" className="botao discreto"
            onClick={() => aoNovaAnalise(c)}
          >
            Fazer a análise crítica
          </button>
        )}
      </p>

      {analise
        ? <QuadroAnalise a={analise} calibracao={c} aoAprovar={carregar} />
        : (
          <Aviso tipo="atencao" titulo="Sem análise crítica">
            A 17025 pede que o laboratório confirme que o resultado da
            calibração atende aos seus requisitos antes do uso (6.4.6).
            Registrar o certificado não é analisá-lo.
          </Aviso>
        )}
    </div>
  );
}

/**
 * A análise crítica de um certificado, aberta.
 *
 * O quadro mostra a conta inteira — EMA, erro, incerteza, margem — porque
 * "conforme" sem os números é o mesmo carimbo que a `0029` veio
 * substituir. Quem audita precisa refazer a conta olhando a tela.
 */
function QuadroAnalise({
  a, calibracao, aoAprovar,
}: { a: Analise; calibracao: Calibracao; aoAprovar(): void }) {
  const comGuarda = a.regra_decisao === 'aceitacao_com_guarda';
  return (
    <div className="bloco">
      <div className="linha-topo">
        <b className="cresce">Análise crítica</b>
        <Selo tom={a.conforme ? 'ok' : 'critico'}>
          {a.conforme ? 'atende ao critério' : 'não atende ao critério'}
        </Selo>
        <Selo tom={a.conclusao === 'adequado' ? 'ok'
          : a.conclusao === 'adequado_com_restricao' ? 'atencao' : 'critico'}>
          {rotulo(a.conclusao)}
        </Selo>
      </div>

      <p className="meta">
        Uso pretendido: {a.uso_pretendido}
        {a.faixa_uso_min !== null && a.faixa_uso_max !== null
          && ` · faixa de uso ${numero(a.faixa_uso_min, 3, true)} a `
             + `${g(a.faixa_uso_max, a.faixa_uso_unidade, 3)}`}
      </p>

      <div className="rola">
        <table className="dados" style={{ marginTop: '.5rem' }}>
          <tbody>
            <tr>
              <th>Erro máximo admissível</th>
              <td className="num">{g(a.ema, a.ema_unidade, 4)}</td>
              <td className="sub">{a.ema_origem}</td>
            </tr>
            <tr>
              <th>Maior erro na faixa de uso</th>
              <td className="num">{g(a.maior_erro, a.ema_unidade, 4)}</td>
              <td className="sub">do certificado</td>
            </tr>
            <tr>
              <th>
                Incerteza
                {a.fator_abrangencia && ` (k = ${numero(a.fator_abrangencia, 2, true)})`}
              </th>
              <td className="num">{g(a.incerteza, a.ema_unidade, 4)}</td>
              <td className="sub">
                {comGuarda ? 'entra na comparação' : 'NÃO entra — aceitação simples'}
              </td>
            </tr>
            <tr className={a.conforme ? undefined : 'linha-alerta'}>
              <th>Margem</th>
              <td className="num">{g(a.margem, a.ema_unidade, 4)}</td>
              <td className="sub">
                EMA − (|erro|{comGuarda ? ' + U' : ''}){' '}
                <Rn codigo="RN-156"
                  diz="Regra de decisão da ISO/IEC 17025, 7.1.3." />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!comGuarda && a.regra_justificativa && (
        <Aviso tipo="atencao" titulo="Aceitação simples">{a.regra_justificativa}</Aviso>
      )}

      {!a.rastreabilidade_ok && (
        <Aviso tipo="erro" titulo="Rastreabilidade dos padrões não confirmada">
          {a.rastreabilidade_nota ?? 'sem nota'}
        </Aviso>
      )}

      {a.fator_correcao !== null && (
        <p className="sub">
          Fator de correção <b>{numero(a.fator_correcao, 6, true)}</b> —{' '}
          {a.correcao_aplicada
            ? <>aplicado em: {a.correcao_onde}</>
            : <>anotado e <b>não aplicado</b> no cálculo</>}
        </p>
      )}

      {a.restricoes && (
        <Aviso tipo="atencao" titulo="Restrições de uso">{a.restricoes}</Aviso>
      )}
      {a.observacoes && <p className="sub">{a.observacoes}</p>}

      <p className="meta">
        Analisada por {a.analisado_por_nome ?? '—'} em {dataHora(a.analisado_em)}
        {a.aprovado_em
          ? ` · aprovada por ${a.aprovado_por_nome ?? '—'} em ${dataHora(a.aprovado_em)}`
          : ' · aguardando aprovação'}
      </p>

      {!a.aprovado_em && (
        <PendenteDeAprovacao a={a} calibracao={calibracao} aoAprovar={aoAprovar} />
      )}
    </div>
  );
}

/**
 * O que falta para a análise valer, e quem pode fazer.
 *
 * Este bloco existia como uma frase — "aguardando aprovação" — e nada
 * mais: a análise podia ser escrita pela tela e só era aprovada por
 * `curl`. Enquanto ela não é aprovada, o certificado não recebe carimbo
 * e o equipamento não entra em ensaio; a tela mostrava o impedimento sem
 * mostrar a saída.
 *
 * Os três motivos de não haver botão são diferentes, e cada um diz o
 * seu, porque "não posso aprovar" sem a razão manda a pessoa procurar o
 * administrador do sistema para um problema que é de processo.
 */
function PendenteDeAprovacao({
  a, calibracao, aoAprovar,
}: { a: Analise; calibracao: Calibracao; aoAprovar(): void }) {
  const { eu, pode } = useSessao();
  const [aberto, setAberto] = useState(false);

  /* RN-265 (0268): na homologação, a conta com os superpoderes aprova a
     própria análise — o banco deixa e marca a análise. */
  const souOAnalista = eu?.usuarioId === a.analisado_por && !eu?.superpoderes;
  const semPdf = !calibracao.arquivo_id;

  if (semPdf) {
    return (
      <Aviso tipo="erro" titulo="Falta o PDF do certificado">
        A aprovação exige o certificado anexado <Rn codigo="RN-155"
          diz="Sem o PDF do certificado não há o que analisar criticamente." />.
        Registre o arquivo no certificado antes de aprovar a análise.
      </Aviso>
    );
  }

  if (souOAnalista) {
    return (
      <Aviso tipo="info" titulo="Você elaborou esta análise.">
        Quem elabora não aprova o próprio trabalho <Rn codigo="RN-157"
          diz="A análise crítica não pode ser aprovada por quem a elaborou." />.
        A aprovação é de outra pessoa com permissão para aprovar certificados
        — e é o banco que recusa, não só esta tela.
      </Aviso>
    );
  }

  if (!pode('equipamento.aprovar_certificado')) {
    return (
      <Aviso tipo="info" titulo="Aguardando quem aprova certificados.">
        Seu perfil não inclui a aprovação de certificados de calibração.
        Enquanto ela não sai, o certificado fica sem carimbo e o equipamento
        não é oferecido em ensaio.
      </Aviso>
    );
  }

  return (
    <>
      <div className="linha" style={{ marginTop: '.6rem' }}>
        <button type="button" className="botao" onClick={() => setAberto(true)}>
          Aprovar a análise crítica
        </button>
      </div>
      {aberto && (
        <FormAprovarAnalise
          analise={a}
          aoFechar={() => setAberto(false)}
          aoPronto={() => { setAberto(false); aoAprovar(); }}
        />
      )}
    </>
  );
}

export function EquipamentoTela() {
  const { id } = useParams<{ id: string }>();
  const [busca] = useSearchParams();
  const aba = abaDoEndereco(busca.get('aba'));
  const [m, setM] = useState<Metrologia | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [impacto, setImpacto] = useState<Impacto | null>(null);
  const [posse, setPosse] = useState<Posse[]>([]);
  /* Quanto trabalho já se apoia neste registro (`0102`) — o formulário de
     correção mostra o número antes de deixar mexer na resolução. */
  const [alcance, setAlcance] = useState<
    { ensaios: number; calibracoes: number; afericoes: number } | null>(null);
  const [trilha, setTrilha] = useState<Elo[] | null>(null);
  const [eventos, setEventos] = useState<Evento[] | null>(null);
  const [software, setSoftware] = useState<Software[] | null>(null);
  /* Erro guardado à parte da lista: «nada registrado» só com resposta
     vazia, nunca quando a API falhou (P-UI-30). */
  const [erroDe, setErroDe] = useState<{ impacto?: unknown; eventos?: unknown; software?: unknown }>({});
  const { pode } = useSessao();
  useCaminho(m ? [
    ...(String(m.equipamento.papel ?? '') === 'padrao'
      ? [{ rotulo: 'Padrões', para: '/equipamentos?familia=padrao' }] : []),
    { rotulo: '', codigo: String(m.equipamento.codigo_patrimonial) },
  ] : null);
  const [form, setForm] = useState<
    { que: 'certificado' | 'sensor' | 'baixa' | 'posse' | 'ficha' | 'software' }
    | { que: 'analise'; calibracao: Calibracao }
    | null>(null);

  const carregar = useCallback(() => {
    api<Metrologia>(`/equipamentos/${id}/metrologia`)
      .then((r) => { setM(r); setErro(null); })
      .catch(setErro);
    // A consulta reversa vem junto, e não ao clicar: o número de ensaios
    // atingidos precisa aparecer NA ABA. A aba «Uso» sem número não diz
    // se vale a pena abrir — e o dia em que valer é o dia de pressa.
    api<Impacto>(`/equipamentos/${id}/impacto`)
      .then((x) => { setImpacto(x); setErroDe((e) => ({ ...e, impacto: undefined })); })
      .catch((x) => setErroDe((e) => ({ ...e, impacto: x })));
    // De quem é o equipamento: oito das treze máquinas Owntec do parque
    // não são da Testesolo, e o laudo não pode dizer que são.
    api<Posse[]>(`/equipamentos/${id}/posse`)
      .then(setPosse).catch(() => setPosse([]));
    api<{ ensaios: number; calibracoes: number; afericoes: number }>(
      `/equipamentos/${id}/alcance`)
      .then(setAlcance).catch(() => setAlcance(null));
    /* A trilha só vem para quem pode auditar; o `catch` silencioso é o
       404/403 de quem não pode, e não um erro a mostrar. */
    api<Elo[]>(`/equipamentos/${id}/trilha`)
      .then(setTrilha).catch(() => setTrilha(null));
    // Onda 351: a linha do tempo única e o histórico de software/firmware.
    api<Evento[]>(`/equipamentos/${id}/linha-do-tempo`)
      .then((x) => { setEventos(x); setErroDe((e) => ({ ...e, eventos: undefined })); })
      .catch((x) => setErroDe((e) => ({ ...e, eventos: x })));
    api<Software[]>(`/equipamentos/${id}/software`)
      .then((x) => { setSoftware(x); setErroDe((e) => ({ ...e, software: undefined })); })
      .catch((x) => setErroDe((e) => ({ ...e, software: x })));
  }, [id]);

  useEffect(carregar, [carregar]);

  if (erro && !m) return <ErroDaApi erro={erro} />;
  if (!m) return <Carregando texto="Carregando a ficha" />;

  const eq = m.equipamento as Record<string, string | number | boolean | null>;
  const vigentes = m.valores.filter((v) => v.vigencia_fim === null);
  const encerrados = m.valores.filter((v) => v.vigencia_fim !== null);
  const papel = String(eq.papel ?? 'equipamento');
  const comBaixa = eq.baixa_tipo !== null && eq.baixa_tipo !== undefined;
  const analisePorCal = new Map(m.analises.map((a) => [a.calibracao_id, a]));
  const blocos = blocosDeGrandeza(m.calibracoes, m.componentes);
  const possevigente = posse.find((p) => p.vigente);

  const fechar = () => { setForm(null); carregar(); };

  return (
    <div className="eq-ficha">
      {/* ── O cabeçalho da moldura (P-UI-33) ───────────────────────── */}
      <div className="cabeca">
        <div className="linha-topo">
          <h1 className="cresce mono">{String(eq.codigo_patrimonial)}</h1>
          <Selo tom={m.situacao.apto ? 'ok' : 'critico'}>
            {m.situacao.apto ? 'Apto' : 'Não pode entrar em ensaio'}
          </Selo>
          <ExcluirDeVez tipo="equipamento" id={String(eq.id)} />
          <MenuDaOs rotulo="Mais ações da ficha">
            <LinkProtegido
              className="item-menu"
              caminho={`/equipamentos/plaquetas?ids=${id}`}
              nomeQueda="plaqueta.html"
            >
              Plaqueta em folha A4
            </LinkProtegido>
            <LinkProtegido
              className="item-menu"
              caminho={`/equipamentos/plaquetas?ids=${id}&formato=rolo&pdf=1`}
              nomeQueda="etiqueta-50x30.html"
            >
              Etiqueta 50 × 30 mm
            </LinkProtegido>
            {pode('equipamento.editar') && !comBaixa && (
              <button type="button" className="item-menu"
                onClick={() => setForm({ que: 'ficha' })}>
                Corrigir a ficha
              </button>
            )}
            {!comBaixa && (
              <button type="button" className="item-menu"
                onClick={() => setForm({ que: 'baixa' })}>
                Dar baixa
              </button>
            )}
          </MenuDaOs>
          <Link
            to={papel === 'padrao' ? '/equipamentos?familia=padrao' : '/equipamentos'}
            className="botao discreto"
          >
            Voltar
          </Link>
        </div>
        <p className="sub">
          {[
            String(eq.nome),
            rotulo(String(eq.categoria)),
            [eq.fabricante, eq.modelo].filter(Boolean).join(' '),
            eq.numero_serie ? `nº de série ${eq.numero_serie}` : '',
            eq.localizacao ? String(eq.localizacao) : '',
            possevigente
              ? (possevigente.proprio ? 'próprio' : `${possevigente.regime_rotulo.toLowerCase()} · ${possevigente.titular ?? ''}`)
              : '',
          ].filter(Boolean).join(' · ')}
          {papel === 'padrao' && ' · padrão de referência'}
          {papel === 'componente' && ' · sensor'}
        </p>
      </div>

      <ErroDaApi erro={erro} />

      {/* ── Posso usar isto hoje? ─────────────────────────────────── */}
      <PossoUsar
        apto={m.situacao.apto}
        motivos={m.situacao.motivos ?? []}
        impedimentos={m.situacao.impedimentos}
        ressalvas={m.situacao.ressalvas}
        categoria={String(eq.categoria)}
        pode={pode}
        aba={aba}
        aoAbrirCertificado={() => setForm({ que: 'certificado' })}
        textoApto={(
          <>
            Cadastro em <b>{rotulo(String(eq.status))}</b>, calibração interna conforme
            {eq.calibravel ? ' e calibração externa vigente' : ''}
            {m.componentes.length > 0
              && `, e ${plural(m.componentes.length, 'sensor', 'sensores')} em dia`}.
          </>
        )}
      />

      <FaixaDeResumo resumo={m.resumo ?? null} calibracoes={m.calibracoes}
        calibravel={eq.calibravel === true} />

      <AbasDaFicha aba={aba} contagem={{
        calibracao: m.calibracoes.length,
        afericao: m.afericoes.length,
        uso: impacto?.total ?? 0,
        historico: eventos?.length ?? 0,
      }} />

      {aba === 'geral' && (
        <>
          <CartoesPorGrandeza blocos={blocos} analisePorCal={analisePorCal} />
        <div className="cartao">
          <h2>Valores vigentes</h2>
          <p className="sub">
            É <b>isto</b> que entra na conta do ensaio — não a dimensão nominal{' '}
            <Rn codigo="RN-153"
              diz="Valores calibrados participam do resultado; são versionados por vigência." />.
            Recalibrar muda o valor de hoje em diante e nunca o de ontem, senão o
            laudo antigo deixa de bater com a própria conta.
          </p>
          {vigentes.length === 0
            ? (
              <p className="nada">
                Nenhum valor calibrado. O ensaio não tem de onde tirar o divisor.
              </p>
            )
            : (
              <div className="rola">
                <table className="dados">
                  <thead>
                    <tr>
                      <th>Grandeza</th><th>Valor</th><th>Desde</th><th>Certificado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vigentes.map((v) => (
                      <tr key={v.id}>
                        <td>
                          {rotulo(v.grandeza)}
                          {/*
                            * Derivado não pode parecer medido.
                            *
                            * O volume do anel sai de π·d²/4·h com o diâmetro
                            * e a altura do próprio certificado (`0104`) —
                            * é geometria, não leitura, e quem assina o laudo
                            * tem direito de ver a diferença sem procurar.
                            */}
                          {v.derivado && (
                            <div className="sub">
                              derivado · {v.observacoes}
                            </div>
                          )}
                        </td>
                        <td className="num">{g(v.valor, v.unidade)}</td>
                        <td>{data(v.vigencia_inicio)}</td>
                        <td>{v.certificado ?? <span className="nada">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          {encerrados.length > 0 && (
            <details style={{ marginTop: '.7rem' }}>
              <summary>{plural(encerrados.length, 'valor encerrado', 'valores encerrados')}</summary>
              <div className="rola">
                <table className="dados" style={{ marginTop: '.5rem' }}>
                  <tbody>
                    {encerrados.map((v) => (
                      <tr key={v.id}>
                        <td>{rotulo(v.grandeza)}</td>
                        <td className="num">{g(v.valor, v.unidade)}</td>
                        <td>{data(v.vigencia_inicio)} a {data(v.vigencia_fim)}</td>
                        <td>{v.certificado ?? <span className="nada">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>

        {/* ── 4a½. As curvas de calibração (0100) ──────────────────── */}

        {m.curvas.length > 0 && (
          <div className="cartao">
            <div className="cartao-cabeca">
              <h2>Curvas de calibração</h2>
              <span className="sub">
                {plural(m.curvas.length, 'curva', 'curvas')} ·{' '}
                {plural(m.curvas.filter((c) => c.vigente).length, 'vigente', 'vigentes')}
              </span>
            </div>

            {m.curvas.map((c) => {
              const aj = ajusteDaCurva(c);
              return (
              <div key={c.curva_id} className="bloco">
                <div className="linha entre">
                  <div>
                    <b>{aj ? `${c.vigente ? 'Curva vigente' : 'Curva encerrada'} · ` : ''}
                      {c.descricao ?? rotulo(c.grandeza)}</b>
                    <div className="sub">
                      versão {c.versao}
                      {c.certificado && ` · certificado ${c.certificado}`}
                      {' · '}
                      {plural(c.pontos.length, 'ponto', 'pontos')}
                    </div>
                  </div>
                  <Selo tom={c.vigente ? 'ok' : 'neutro'}>
                    {c.vigente ? 'vigente' : 'encerrada'}
                  </Selo>
                </div>

                {/*
                  * A faixa vem antes da tabela de propósito. Fora dela não
                  * há medida, e a pergunta que o técnico faz olhando uma
                  * curva é «o meu ensaio caiu dentro?».
                  */}
                {c.x_min !== null && !aj && (
                  <p className="sub">
                    Medida de {numero(c.x_min, 4, true)} a{' '}
                    {numero(c.x_max, 4, true)} {c.entrada_unidade}.
                    {' '}Fora dessa faixa não há medida.
                  </p>
                )}

                {/*
                  * A curva do picnômetro (RN-259): o que vale é a parábola
                  * ajustada pelo banco aos pontos; o polinômio impresso no
                  * certificado é registro e comparação, nunca entra na conta.
                  */}
                {aj && (
                  <>
                    <ResumoDoAjuste {...aj} />
                    <GraficoDaCurva
                      pontos={c.pontos.map((p) => ({ x: Number(p.x), y: Number(p.y) }))}
                      a={aj.a} xMin={aj.xMin} xMax={aj.xMax}
                      impresso={lerPolinomio(c.funcao_texto)} />
                  </>
                )}

                {c.funcao_texto && (
                  <p className="mono">
                    {aj ? 'Polinômio do certificado (só comparação): ' : ''}
                    {c.funcao_texto}
                    {c.r2 !== null && ` · R² = ${numero(c.r2, 4, true)}`}</p>
                )}

                <div className="rola">
                  <table className="dados">
                    <thead>
                      <tr>
                        <th>{rotulo(c.variavel_entrada ?? 'entrada')} ({c.entrada_unidade})</th>
                        <th className="num">{rotulo(c.grandeza)} ({c.saida_unidade})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.pontos.map((p) => (
                        <tr key={p.x}>
                          <td className="mono">{numero(p.x, 4, true)}</td>
                          <td className="num mono">{numero(p.y, 4, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/*
                  * `interpolacao` é regra de cálculo, e enquanto a
                  * Testesolo não a validar o sistema diz isso em voz alta
                  * em vez de escolher por ela (§25).
                  */}
                {c.interpolacao === 'nao_definida' && (
                  <Aviso tipo="atencao" titulo="Como ler entre os pontos ainda não foi definido.">
                    O certificado publica a função ajustada acima. Escolher
                    entre ela, a interpolação linear e a tabela da norma é
                    regra de cálculo, e precisa de validação da Testesolo
                    antes de entrar em ensaio.
                  </Aviso>
                )}
              </div>
              );
            })}
          </div>
        )}

          <div className="cartao">
            <div className="cartao-cabeca">
              <h2>Últimos acontecimentos</h2>
              <Link to="?aba=historico" className="botao discreto">Ver a linha do tempo inteira</Link>
            </div>
            <LinhaDoTempo eventos={eventos} limite={6} erro={erroDe.eventos} />
          </div>
        </>
      )}

      {aba === 'calibracao' && (
        <>
        {/* ── 5. Calibração externa, agrupada por GRANDEZA ───────────── */}

        <SecaoCalibracao
          m={m}
          comBaixa={comBaixa}
          calibravel={eq.calibravel === true}
          analisePorCal={analisePorCal}
          aoNovoCertificado={() => setForm({ que: 'certificado' })}
          aoNovaAnalise={(c) => setForm({ que: 'analise', calibracao: c })}
          aoNovoSensor={() => setForm({ que: 'sensor' })}
          papel={papel}
          carregar={carregar}
        />

          {!eq.calibravel && m.calibracoes.length === 0 && m.componentes.length === 0 && (
            <p className="nada">
              Este equipamento não é calibrado fora: o que o sustenta é a
              calibração interna (aba Calibrações internas).
            </p>
          )}
        </>
      )}

      {aba === 'afericao' && (
        <>
        {/* ── 4. De onde esse número saiu? ───────────────────────────── */}

        <div className="cartao">
          {/*
            * O cartão ganhou AÇÃO.
            *
            * «não estou conseguindo lançar novos valores de massa ou
            *  dimensões para anéis, moldes, etc. O que aconteceu?»
            *  — Humberto, 01/09/2026.
            *
            * O que aconteceu foi isto: a ficha mostrava as aferições e não
            * tinha por onde começar uma. A campanha existia, mas só se
            * chegava a ela pelo menu de verificações — e quem está olhando
            * a ficha de um anel não pensa «vou ao menu de verificações»,
            * pensa «vou aferir este anel».
            *
            * O botão leva à folha da CATEGORIA dele, e não a um formulário
            * de um objeto só: a campanha é o gesto do laboratório, e aferir
            * um anel sozinho é a exceção, não a regra.
            */}
          <div className="linha-topo">
            <h2 className="cresce">Calibrações internas</h2>
            {pode('equipamento.verificar') && (
              <Link
                className="botao secundario"
                to={`/equipamentos/aferir-massas?categoria=${
                  encodeURIComponent(String(eq.categoria))}`}
              >
                + Calibração interna
              </Link>
            )}
          </div>
          {m.afericoes.length === 0
            ? (
              <p className="nada">
                Nenhuma calibração interna registrada. O botão acima abre a folha da
                campanha desta categoria, já com este objeto na lista.
              </p>
            )
            : m.afericoes.map((a) => {
              const itens = m.itens.filter((i) => i.verificacao_id === a.id);
              const naoConformes = Number(a.itens_nao_conformes);
              return (
                <div key={a.id} className="bloco">
                  <div className="linha-topo">
                    <b className="cresce">
                      {a.certificado ?? 'sem certificado'}
                      {a.certificado_titulo && ` — ${a.certificado_titulo}`}
                    </b>
                    {/*
                      * Três estados, como o `conforme` do banco. «APROVADO»
                      * num certificado cujos itens não têm critério dizia
                      * mais do que a aferição mediu.
                      */}
                    <Selo tom={a.conforme === false ? 'critico'
                      : a.conforme === true ? 'ok' : 'neutro'}>
                      {a.conforme === false ? 'não conforme'
                        : a.conforme === true ? 'conforme'
                          : 'medido, sem critério'}
                    </Selo>
                  </div>
                  <div className="meta">
                    {data(a.data_afericao ?? a.data)}
                    {a.responsavel && ` · ${a.responsavel}`}
                    {a.proxima_verificacao && ` · próxima em ${data(a.proxima_verificacao)}`}
                    {naoConformes > 0 && ` · ${plural(naoConformes, 'item', 'itens')} fora da tolerância`}
                  </div>

                  {a.uso_autorizado && (
                    <Aviso tipo="atencao" titulo="Uso autorizado mesmo assim">
                      {a.uso_justificativa ?? 'sem justificativa registrada'}
                    </Aviso>
                  )}

                  {itens.length > 0 && (
                    <div className="rola">
                      <table className="dados" style={{ marginTop: '.5rem' }}>
                        <thead>
                          <tr>
                            <th>Item</th><th>Medido</th><th>Instrumento</th>
                            <th>Tolerância</th><th>Veredito</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itens.map((i) => (
                            <tr
                              key={i.id}
                              className={i.veredito === 'fora' ? 'linha-alerta' : undefined}
                            >
                              <td>
                                {i.simbolo && <span className="mono">{i.simbolo} </span>}
                                {i.item}
                                {i.derivado && <span className="sub"> derivado</span>}
                              </td>
                              {/*
                                * As casas decimais são as do INSTRUMENTO.
                                * Paquímetro de 0,01 mm lê duas; mostrar três
                                * afirma uma exatidão que ninguém mediu.
                                */}
                              <td className="num">
                                {i.valor_medido === null
                                  ? <span className="nada">—</span>
                                  : g(i.valor_medido, i.unidade,
                                    i.padrao_casas_decimais ?? 3)}
                              </td>
                              <td className="sub">
                                {i.padrao
                                  ? (
                                    <>
                                      {i.padrao}
                                      {i.padrao_certificado && (
                                        <span className="mono"> · {i.padrao_certificado}</span>
                                      )}
                                    </>
                                  )
                                  : <span className="nada">não informado</span>}
                              </td>
                              <td className="num"><Faixa i={i} /></td>
                              <td>
                                {i.veredito === 'conforme'
                                  ? <Selo tom="ok">Conforme</Selo>
                                  : i.veredito === 'fora'
                                    ? <Selo tom="critico">Fora</Selo>
                                    : (
                                      <>
                                        <Selo tom="neutro">Sem critério</Selo>
                                        {i.motivo_sem_criterio && (
                                          <div className="sub">
                                            {i.motivo_sem_criterio
                                              === 'tolerancia_nao_impressa'
                                              ? 'o certificado não imprimiu a tolerância'
                                              : 'a norma não trata disso'}
                                          </div>
                                        )}
                                      </>
                                    )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {a.observacoes && <p className="sub">{a.observacoes}</p>}
                </div>
              );
            })}
        </div>

        </>
      )}

      {aba === 'manutencao' && (
        <>
        {/* ── 6. Uso excepcional, manutenção e fotografias ──────────── */}

        {/*
          * A autorização vem PRIMEIRO das três, e só aparece quando há
          * impedimento (ou histórico). É a resposta à pergunta que o
          * aviso vermelho lá em cima acabou de levantar: «não pode ser
          * usado» — e agora?
          */}
        <SecaoAutorizacoes
          equipamento={id!}
          impedido={!m.situacao.apto}
          aoMudar={carregar}
        />

        <SecaoManutencao
          equipamento={id!}
          codigo={String(eq.codigo_patrimonial)}
          /* Encerrar uma manutenção pode mudar a resposta do topo da ficha:
             «posso usar isto hoje?». Recarregar a metrologia é o que faz o
             aviso lá em cima concordar com o que acabou de acontecer aqui. */
          aoMudar={carregar}
        />

        <SecaoFotos equipamento={id!} codigo={String(eq.codigo_patrimonial)} />

        </>
      )}

      {aba === 'uso' && (
        <ImpactoNaAba codigo={String(eq.codigo_patrimonial)} impacto={impacto}
          erro={erroDe.impacto} />
      )}

      {aba === 'historico' && (
        <div className="cartao">
          <h2>Linha do tempo</h2>
          <p className="sub">
            Tudo o que aconteceu com o equipamento, numa lista só: calibração,
            análise, calibração interna, manutenção, autorização de uso, posse,
            software e baixa. A trilha campo a campo da ficha fica em
            Identificação e posse.
          </p>
          <LinhaDoTempo eventos={eventos} comFiltro erro={erroDe.eventos} />
        </div>
      )}

      {aba === 'identificacao' && (
        <>
        {/* ── Identificação, no fim: é consulta, não decisão ─────────── */}

        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>Identificação</h2>
            {/*
              * O botão de correção fica aqui, e não num menu.
              *
              * 223 equipamentos entraram por carga: faixa, resolução e
              * grandeza medida vieram de certificado lido por ferramenta, e
              * ferramenta erra. Sem esta porta, o caminho que sobra é apagar
              * e cadastrar de novo — levando junto a trilha, as calibrações
              * e as aferições penduradas no registro.
              */}
            {pode('equipamento.editar') && !comBaixa && (
              <button
                type="button" className="botao discreto"
                onClick={() => setForm({ que: 'ficha' })}
              >
                Corrigir a ficha
              </button>
            )}
          </div>
          <div className="rola">
            <table className="dados">
              <tbody>
                <tr><th>Fabricante</th><td>{eq.fabricante ?? <span className="nada">—</span>}</td></tr>
                <tr><th>Modelo</th><td>{eq.modelo ?? <span className="nada">—</span>}</td></tr>
                <tr><th>Número de série</th><td>{eq.numero_serie ?? <span className="nada">—</span>}</td></tr>
                <tr><th>Localização</th><td>{eq.localizacao ?? <span className="nada">—</span>}</td></tr>
                {/* De onde veio (0093). Sem procedência, a pergunta da
                    auditoria «desde quando é seu?» não tem resposta. */}
                <tr><th>Aquisição</th><td>
                  {(eq as any).data_aquisicao
                    ? <>{data((eq as any).data_aquisicao)}
                        {(eq as any).nota_fiscal && <> · NF {(eq as any).nota_fiscal}</>}
                        {(eq as any).fornecedor && <span className="sub">{(eq as any).fornecedor}</span>}</>
                    : <span className="nada">sem procedência registrada</span>}
                </td></tr>
                {/*
                  * A idade, quando se sabe (`0110`).
                  *
                  * Só aparece quando há data: uma linha «Fabricação —» em
                  * 200 fichas é ruído, e boa parte do parque é antiga e não
                  * tem essa data em lugar nenhum. Quem quiser preencher usa
                  * «Corrigir a ficha».
                  */}
                {(eq as any).data_fabricacao && (
                  <tr><th>Fabricação</th><td>
                    {/*
                      * Mês e ano, nunca o dia (`0117`). O primeiro do mês é
                      * convenção de armazenamento; imprimi-lo faria da
                      * convenção uma informação que ninguém leu.
                      */}
                    {mesAno((eq as any).data_fabricacao)}
                    <span className="sub">
                      {' '}({Math.floor(
                        (Date.now() - new Date(String((eq as any).data_fabricacao)).getTime())
                        / 31557600000)} anos)
                    </span>
                  </td></tr>
                )}
                {eq.grandeza_medida && (
                  <tr><th>Grandeza medida</th><td>{rotulo(String(eq.grandeza_medida))}</td></tr>
                )}
                {eq.posicao && <tr><th>Posição</th><td>{String(eq.posicao)}</td></tr>}
                <tr>
                  <th>Capacidade</th>
                  <td className="num">
                    {eq.capacidade_nominal
                      ? g(eq.capacidade_nominal, String(eq.capacidade_unidade ?? ''), 3)
                      : <span className="nada">—</span>}
                  </td>
                </tr>
                <tr>
                  <th>Resolução</th>
                  <td className="num">
                    {eq.resolucao
                      ? g(eq.resolucao, String(eq.resolucao_unidade ?? ''), 4)
                      : <span className="nada">—</span>}
                  </td>
                </tr>
                <tr>
                  <th>Faixa</th>
                  <td className="num">
                    {eq.faixa_min !== null && eq.faixa_max !== null
                      ? `${numero(eq.faixa_min, 3, true)} a `
                        + `${numero(eq.faixa_max, 3, true)} ${eq.faixa_unidade ?? ''}`
                      : <span className="nada">—</span>}
                  </td>
                </tr>
                {/*
                  * A periodicidade não anda sem dizer de onde veio (`0108`).
                  *
                  * Este número decide o vencimento, e vencimento impede
                  * ensaio. Ele entrou por três caminhos que esta ficha não
                  * distinguia: o certificado, a planilha da casa e dedução do
                  * histórico. Deduzido com cara de medido é pior que campo em
                  * branco — campo em branco alguém preenche.
                  */}
                <tr>
                  <th>Periodicidade</th>
                  <td>
                    {eq.periodicidade_calibracao_meses
                      ? (
                        <>
                          {String(eq.periodicidade_calibracao_meses)} meses{' '}
                          {eq.periodicidade_origem === 'inferida'
                            ? <Selo tom="atencao">Deduzida do histórico</Selo>
                            : (
                              <span className="sub">
                                {ORIGEM_DA_PERIODICIDADE[
                                  String(eq.periodicidade_origem)] ?? '—'}
                              </span>
                            )}
                        </>
                      )
                      : <span className="nada">—</span>}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {eq.observacoes && <p className="sub">{String(eq.observacoes)}</p>}
        </div>

          <SecaoSoftware
            lista={software}
            erro={erroDe.software}
            podeRegistrar={pode('equipamento.editar') && !comBaixa}
            aoRegistrar={() => setForm({ que: 'software' })}
          />
        {/* ── 4b. Posse: de quem é o equipamento, e desde quando ─────── */}

        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>Posse</h2>
            {!comBaixa && (
              <button
                type="button" className="botao discreto"
                onClick={() => setForm({ que: 'posse' })}
              >
                + Regime
              </button>
            )}
          </div>
          <p className="sub">
            Regime não é campo: é linha do tempo. Um ensaio de 25/08 e outro de
            27/08 na mesma prensa podem ter sido feitos sob regimes diferentes, e
            cada laudo tem que dizer a verdade do seu dia.
          </p>

          {posse.length === 0
            ? (
              <p className="nada">
                Nenhum regime registrado — não dá para responder «desde quando é
                seu?».
              </p>
            )
            : (
              <div className="rola">
                <table className="lista">
                  <thead>
                    <tr>
                      <th>Regime</th><th>Titular</th><th>Documento</th>
                      <th>Desde</th><th>Até</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {posse.map((p) => (
                      <tr key={p.id} className={p.vigente ? 'destaque' : undefined}>
                        <td>{p.regime_rotulo}</td>
                        <td>
                          {p.proprio
                            ? <span className="sub">Testesolo</span>
                            : (p.titular ?? <span className="nada">—</span>)}
                        </td>
                        <td className="mono">
                          {p.documento ?? <span className="nada">—</span>}
                        </td>
                        <td>{data(p.vigencia_inicio)}</td>
                        <td>
                          {p.vigencia_fim
                            ? data(p.vigencia_fim)
                            : <span className="sub">vigente</span>}
                        </td>
                        <td>
                          {p.vigente && !p.proprio && (
                            <Selo tom="atencao">Não é nosso</Selo>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        {/* ── Baixa ──────────────────────────────────────────────────── */}

        {comBaixa && (
          <div className="cartao">
            <h2>Baixa</h2>
            <table className="dados">
              <tbody>
                <tr><th>Tipo</th><td>{rotulo(String(eq.baixa_tipo))}</td></tr>
                <tr><th>Motivo</th><td>{String(eq.baixa_motivo ?? '—')}</td></tr>
                <tr>
                  <th>Registrada em</th>
                  <td>{eq.baixa_em ? dataHora(String(eq.baixa_em)) : '—'}</td>
                </tr>
              </tbody>
            </table>
            <p className="rodape-regra">
              O histórico acima continua inteiro e consultável. A baixa impede o
              futuro — calibração externa e interna novas são recusadas{' '}
              <Rn codigo="RN-159"
                diz="Equipamento com baixa não recebe calibração externa nem interna nova." />.
            </p>
          </div>
        )}

        {/* ── 5b. A ficha conta a própria história (CA-21) ─────────── */}

        {trilha && trilha.length > 0 && (
          <div className="cartao">
            <details>
              <summary>
                <b>Histórico da ficha</b>
                <span className="sub"> · {trilha.length} alteração(ões)</span>
              </summary>
              <p className="sub" style={{ marginTop: '.6rem' }}>
                Desde que a ficha pode ser corrigida, «a resolução desta balança
                é 0,001 g» virou um fato com data. Quem lê um laudo antigo
                precisa poder ver que o número mudou depois — quem mudou, quando
                e por quê.
              </p>
              <div className="rola">
                <table className="dados">
                  <thead>
                    <tr>
                      <th>Quando</th><th>Quem</th><th>O que mudou</th><th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trilha.map((e, i) => (
                      <tr key={`${e.datahora}-${i}`}>
                        <td>{dataHora(e.datahora)}</td>
                        <td>
                          {e.usuario_nome ?? e.usuario_email
                            ?? <span className="nada">sistema</span>}
                          {e.perfil && <div className="sub">{rotulo(e.perfil)}</div>}
                        </td>
                        <td>
                          {e.operacao === 'INSERT'
                            ? <span className="sub">cadastro</span>
                            : (e.campos_alterados ?? []).length === 0
                              ? <span className="nada">—</span>
                              : (e.campos_alterados ?? []).map((c) => (
                                <div key={c}>
                                  <b>{rotulo(c)}</b>
                                  {': '}
                                  <span className="mono">
                                    {String(e.valor_anterior?.[c] ?? '—')}
                                  </span>
                                  {' → '}
                                  <span className="mono">
                                    {String(e.valor_novo?.[c] ?? '—')}
                                  </span>
                                </div>
                              ))}
                        </td>
                        <td className="sub">
                          {e.motivo ?? <span className="nada">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}

        <p className="rodape-regra">
          <LinkProtegido
            className="botao discreto"
            caminho={`/equipamentos/plaquetas?ids=${id}`}
            nomeQueda="plaqueta.html"
          >
            Plaqueta em folha A4
          </LinkProtegido>
          {' '}
          <LinkProtegido
            className="botao discreto"
            caminho={`/equipamentos/plaquetas?ids=${id}&formato=rolo&pdf=1`}
            nomeQueda="etiqueta-50x30.html"
          >
            Etiqueta 50 × 30 mm
          </LinkProtegido>
          {' '}
          A plaqueta leva ao código patrimonial, não a um endereço: ela fica
          colada no equipamento por anos, e endereço muda. Quem resolve o
          código é o sistema, que exige conta.
        </p>

        </>
      )}

      {/* ── Diálogos ───────────────────────────────────────────────── */}

      {form?.que === 'software' && (
        <FormSoftware equipamento={id!} aoFechar={fechar} />
      )}
      {form?.que === 'posse' && (
        <FormPosse equipamento={id!} aoFechar={fechar} />
      )}

      {form?.que === 'certificado' && (
        <FormCertificado equipamento={id!} aoFechar={fechar} />
      )}
      {form?.que === 'analise' && (
        <FormAnaliseCritica calibracao={form.calibracao} aoFechar={fechar} />
      )}
      {form?.que === 'sensor' && (
        <FormSensor equipamento={id!} aoFechar={fechar} />
      )}
      {form?.que === 'baixa' && (
        <FormBaixa
          equipamento={id!}
          codigo={String(eq.codigo_patrimonial)}
          aoFechar={fechar}
        />
      )}
      {form?.que === 'ficha' && (
        <FormCorrigirFicha
          equipamento={eq}
          alcance={alcance}
          aoFechar={() => setForm(null)}
          aoPronto={fechar}
        />
      )}
    </div>
  );
}

/**
 * A pergunta do pior dia do laboratório: o equipamento voltou fora de
 * tolerância — o que já saiu porta afora medido com ele? O vínculo guarda
 * QUAL certificado estava vigente em cada uso, então recalibrar hoje não
 * reescreve a história de ontem.
 */
function ImpactoNaAba({ codigo, impacto, erro }: {
  codigo: string; impacto: Impacto | null; erro?: unknown;
}) {
  /* «Nenhum ensaio» só com a resposta vazia: no equipamento fora de
     tolerância, uma ausência falsa é o pior erro possível (RN-152). */
  if (erro) return <div className="cartao"><h2>Uso e impacto de {codigo}</h2><ErroDaApi erro={erro} /></div>;
  if (impacto === null) return <Carregando texto="Carregando o impacto" />;
  return (
    <div className="cartao">
      <h2>Uso e impacto de {codigo}</h2>
      <p className="sub">
        Tudo que foi ensaiado com este equipamento, com o certificado que
        estava vigente em cada uso{' '}
        <Rn codigo="RN-152"
          diz="Equipamento fora de tolerância exige identificar todos os ensaios executados desde a última calibração válida." />.
      </p>

      {impacto && impacto.emitidos > 0 && (
        <Aviso tipo="atencao" titulo={`${plural(impacto.emitidos, 'relatório já emitido', 'relatórios já emitidos')}`}>
          Exigem decisão do Responsável Técnico: reavaliar, emitir revisão ou
          comunicar o cliente.
        </Aviso>
      )}

      <div className="rola">
        <table className="dados">
          <thead>
            <tr>
              <th>OS</th><th>Amostra</th><th>Usado em</th>
              <th>Ensaio</th><th>Relatório</th>
            </tr>
          </thead>
          <tbody>
            {(impacto?.usos ?? []).map((u) => (
              <tr key={u.ensaio_id} className={u.relatorio ? 'linha-alerta' : undefined}>
                <td className="mono">{u.os ?? <span className="nada">—</span>}</td>
                {/* RN-248: o nome do cliente e o código; a OS tem coluna própria. */}
                <td>{u.amostra
                  ? <NomeDaAmostra amostra={{ codigo: u.amostra, identificacao: u.amostra_identificacao }} />
                  : <span className="nada">—</span>}</td>
                <td>
                  {u.datahora_uso ? dataHora(u.datahora_uso) : <span className="nada">—</span>}
                </td>
                <td>{rotulo(u.status_ensaio)}</td>
                <td>
                  {u.relatorio
                    ? <Selo tom="critico">{u.relatorio}</Selo>
                    : <Selo tom="neutro">Não emitido</Selo>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {impacto.usos.length === 0 && (
        <p className="nada">Nenhum ensaio registrado com este equipamento.</p>
      )}
    </div>
  );
}

/** Uma versão de software ou firmware (`0275`, RN-272). */
interface Software {
  id: string; tipo: string; nome: string; versao: string;
  desde: string; ate: string | null; vigente: boolean;
  motivo: string; registrado_por_nome: string | null; registrado_em: string;
}

/**
 * Software e firmware, com o período de cada versão (onda 351).
 *
 * Só se acrescenta: a troca é linha nova e a anterior fica com o período em
 * que valeu — quem refaz a conta de um laudo antigo precisa saber que
 * versão estava na máquina naquele dia (ISO/IEC 17025, 6.4.13).
 */
function SecaoSoftware({ lista, erro, podeRegistrar, aoRegistrar }: {
  lista: Software[] | null; erro?: unknown; podeRegistrar: boolean; aoRegistrar(): void;
}) {
  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Software e firmware</h2>
        {podeRegistrar && (
          <button type="button" className="botao discreto" onClick={aoRegistrar}>
            Registrar troca
          </button>
        )}
      </div>
      {erro ? <ErroDaApi erro={erro} />
        : lista === null ? <Carregando texto="Carregando o software e o firmware" />
        : lista.length === 0
        ? (
          <p className="nada">
            Nenhuma versão registrada. Equipamento com controlador ou programa
            de aquisição registra aqui a versão instalada.
          </p>
        )
        : (
          <div className="rola">
            <table className="dados">
              <thead>
                <tr>
                  <th>Programa</th><th>Versão</th><th>Período</th><th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((x) => (
                  <tr key={x.id} className={x.vigente ? 'marcada' : undefined}>
                    <td>
                      {x.nome || (x.tipo === 'firmware' ? 'Firmware do equipamento' : 'Software')}
                      <div className="sub">{rotulo(x.tipo)}</div>
                    </td>
                    <td className="mono">{x.versao}</td>
                    <td>
                      {data(x.desde)}
                      {' → '}
                      {x.ate ? data(x.ate) : <Selo tom="ok">vigente</Selo>}
                    </td>
                    <td className="sub">
                      {x.motivo}
                      {x.registrado_por_nome && ` · ${x.registrado_por_nome}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
